import React from 'react';
import { Helmet } from 'react-helmet';
import AdSenseBlock from "../components/AdSenseBlock";
import { AD_SLOTS } from "../constants/adSlots";
import { Link } from "react-router-dom";
import { createRakutenLink } from "../utils/rakuten";
import { createAmazonLink } from "../utils/amazon";

const HowToUse = () => (
  <div
    style={{
      padding: '1rem',
      lineHeight: 1.5,
      textAlign: 'left',
      maxWidth: '820px',
      margin: '0 auto'
    }}
  >

    {/* SEO */}
    <Helmet>
      <title>Trail Run Simulator の使い方｜トレラン完走計画の作り方</title>
      <meta
        name="description"
        content="GPXと走力からトレラン完走計画を自動生成。初心者でも迷わず使える Trail Run Simulator の使い方をわかりやすく解説します。"
      />
    </Helmet>

    <h2>トレラン完走計画の作り方（TrailRunSim 使い方ガイド）</h2>

    {/* -----------------------------------------------------
        Quick Guide（簡易説明）
       ----------------------------------------------------- */}
    <AdSenseBlock adSlot={AD_SLOTS.HOWTO_TOP} />

<div
  style={{
    padding: '1.4rem',
    background: 'rgba(255,255,255,0.65)',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    margin: '1.2rem 0 1.8rem',
    fontFamily: 'SF Pro Text, -apple-system, BlinkMacSystemFont',
    lineHeight: 1.6
  }}
>
  <h3 style={{ margin: '0 0 0.8rem', fontSize: '1.2rem' }}>
    TrailRunSim の使い方
  </h3>

  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '1rem'
  }}>
    {/* Step 1 */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>📍</div>
      <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>１．レースを選ぶ</div>
    </div>

    {/* Step 2 */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>🏃‍♂️</div>
      <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>２．走力を入力</div>
    </div>

    {/* Step 3 */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>⏱️</div>
      <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>３．休憩時間を設定</div>
    </div>

    {/* Step 4 */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>⚙️</div>
      <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>４．シミュレーション実行</div>
    </div>

    {/* Step 5 */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>📊</div>
      <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>５．結果を確認</div>
    </div>
  </div>

  <Link
    to="/"
    style={{
      display: 'inline-block',
      marginTop: '1.4rem',
      padding: '0.8rem 1.2rem',
      background: 'rgba(255,255,255,0.75)',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      textDecoration: 'none',
      fontWeight: 600,
      color: '#2c7be5'
    }}
  >
    シミュレーションを開く →
  </Link>
</div>



    {/* 動画 }
    <div className="help-video" style={{ marginBottom: '1.5rem' }}>
      <video autoPlay muted loop playsInline controls width="75%">
        <source src="/videos/help-simulation.mp4" type="video/mp4" />
        お使いのブラウザは動画再生に対応していません。
      </video>
    </div>
    { 動画 */}

    {/* <AdSenseBlock adSlot={AD_SLOTS.HOWTO_TOP} /> */}

    {/* -----------------------------------------------------
        ステップ1：レース選択
       ----------------------------------------------------- */}
    <details style={{ marginTop: '1.6rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        ステップ1：レースを選ぶ（GPXファイルの読み込み）
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          TrailRunSim では主要レースの GPX をあらかじめ登録しているため、
          一覧から選ぶだけでコースの全体像をすぐに確認できます。
        </p>

        <ul style={{ marginTop: '0.4rem' }}>
          <li>ボタンを押すだけで GPX を自動読み込み</li>
          <li>距離・累積標高・難易度（km-effort）が一目でわかる</li>
          <li>公式サイトへのリンクも自動表示</li>
          <li>エイド情報（名称・距離）もすぐ確認可能</li>
          <li>読み込み完了後に選択中のファイルが表示されます</li>
        </ul>

        <p style={{ marginTop: '0.8rem', color: '#444' }}>
          ご自身の GPX ファイルを読み込むこともできます。
          練習コースやローカルレースにも対応できます。
        </p>

        <p style={{ marginTop: '0.6rem', fontSize: '0.9rem', color: '#555' }}>
          一覧に表示するレースは、<Link to="/Contact">お問い合わせ</Link> からリクエストいただければ追加します。
        </p>

        {/* km-effort 説明 */}
        <div
          style={{
            marginTop: '1rem',
            padding: '0.8rem',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
            lineHeight: 1.5
          }}
        >
          <strong style={{ fontSize: '0.95rem' }}>レース難易度（km-effort）とは？</strong>
          <p style={{ margin: '0.4rem 0', color: '#444' }}>
            km-effort は「距離（km） + 累積標高 ÷ 100」で算出される、
            レースの総合負荷を表す ITRA（国際トレイルランニング協会）の基準です。
          </p>

          <ul style={{ marginTop: '0.4rem' }}>
            <li>0〜24：XXS（超初級）</li>
            <li>25〜44：XS（初級）</li>
            <li>45〜74：S（中級）</li>
            <li>75〜114：M（中上級）</li>
            <li>115〜154：L（上級）</li>
            <li>155〜209：XL（超上級）</li>
            <li>210以上：XXL（ウルトラ級）</li>
          </ul>

          <p style={{ marginTop: '0.6rem', color: '#555', fontSize: '0.9rem' }}>
            この値を把握しておくことで、より現実的な完走計画が作れます。
          </p>
        </div>

        {/* ★ ITRA Finisher Level 説明（km-effort と同じ構造） */}
        <div
          style={{
            marginTop: '1rem',
            padding: '0.8rem',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
            lineHeight: 1.5
          }}
        >
          <strong style={{ fontSize: '0.95rem' }}>ITRA Finisher Levelとは？</strong>
          <p style={{ margin: '0.4rem 0', color: '#444' }}>
            Finisher Levelは、ITRA パフォーマンスインデックス（PI）をもとに、「制限時間ぎりぎりで完走するために最低限必要な走力」を推定した指標です。
          </p>

          <ul style={{ marginTop: '0.4rem' }}>
            <li>自身の PI と比較することで、完走可能性の目安になります</li>
            <li>レースの“完走難易度”を客観的に把握できます</li>
          </ul>

          <p style={{ marginTop: '0.6rem', color: '#555', fontSize: '0.9rem' }}>
            自分のPIと照らし合わせて、完走の可能性を簡単に確認できます。
          </p>
        </div>
      </div>
    </details>


    {/* -----------------------------------------------------
        ステップ2：走力情報
      ----------------------------------------------------- */}
    <details style={{ marginTop: '1.6rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        ステップ2：走力を入力（あなたのペースを設定）
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>

        {/* 導入文 */}
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          完走計画の心臓部が<strong>走力設定</strong>です。
          ITRA PI と普段のジョグペースを入力すると、上り・下り・疲労によるペース変化を自動計算します。
        </p>

        <ul style={{ marginTop: '0.4rem' }}>
          <li>ITRAパフォーマンスインデックス（長時間走行の持久力）</li>

          {/* ▼ ITRA PI 説明カード（あなたの添付内容を反映） */}
<div
  style={{
    marginTop: '8px',
    padding: '0.8rem',
    background: 'rgba(255,255,255,0.6)',
    borderRadius: '10px',
    fontSize: '0.85rem',
    color: '#555',
    lineHeight: 1.6
  }}
>
  <strong style={{ fontSize: '0.95rem', color: '#333' }}>
    ITRA パフォーマンスインデックス（1〜1000）
  </strong>

  <p style={{ margin: '0.4rem 0' }}>
    このシミュレータでは、<strong>持久力レベル</strong>の目安になります。
  </p>

  <p style={{ margin: '0.4rem 0' }}>
    ITRA PI は過去のレース結果から算出される<strong>総合的な走力指数</strong>ですが、
    このシミュレータでは<strong>無理なく走れるペースをどれだけ維持できるかという持久力</strong>の数値として計算しています。
  </p>

  <strong style={{ fontSize: '0.9rem' }}>▼ 参考の目安</strong>
  <ul style={{ marginTop: '0.4rem', paddingLeft: '1.2rem' }}>
    <li>826以上：国際大会入賞レベル</li>
    <li>726〜825：国内大会入賞レベル</li>
    <li>551〜725：上級者</li>
    <li>301〜550：中級者</li>
    <li>300以下：初級者・登山レベル</li>
  </ul>

  <a
    href="https://itra.run/"
    target="_blank"
    rel="noopener noreferrer"
    className="itra-link"
    style={{
      marginTop: '6px',
      display: 'inline-block',
      color: '#0066cc',
      fontWeight: 600,
      textDecoration: 'none'
    }}
  >
    ITRA公式サイトで自分のPIを調べる ↗
  </a>
</div>


          {/* ▼ 基準ペース */}
          <li>
            無理なく走れるペース（シミュレーションで使う基準ペース）
            <div
              style={{
                marginTop: '6px',
                padding: '0.6rem',
                background: 'rgba(255,255,255,0.6)',
                borderRadius: '10px',
                fontSize: '0.8rem',
                color: '#555',
                lineHeight: 1.5
              }}
            >
              <p style={{ margin: '0.4rem 0', color: '#555' }}>
                フルマラソン完走タイムは、ボリュームゾーン（中級者層）の方の走力の参考になります。「フルをどれくらいで走れるか」を把握している方は、基準ペースを決める際の参考にしてください。
              </p>

              <strong style={{ fontSize: '0.85rem' }}>▼ フルマラソン完走タイムからの換算式</strong><br />
              無理なく走れるペース（MM:SS/km）＝ フルマラソン完走タイム ÷ 42.195km
              <br />

              <strong style={{ fontSize: '0.85rem' }}>▼ 代表例</strong><br />
              5.5時間 → 7'49/km<br />
              5.0時間 → 7'06/km<br />
              4.5時間 → 6'24/km<br />
              4.0時間 → 5'41/km<br />
              3.5時間 → 4'59/km<br />
              3.0時間 → 4'16/km<br />
              初心者の目安：7'00〜8'30/km
            </div>
          </li>

          <li>レース当日の頑張り度（攻める(S)～抑える(D)の補正）</li>
        </ul>

        {/* 補足 */}
        <p style={{ marginTop: '0.8rem', color: '#555', fontSize: '0.9rem' }}>
          走力設定は、区間ペース・エイド到着予測・関門判定に大きく影響します。
        </p>
      </div>
    </details>

    {/* -----------------------------------------------------
        ステップ3：エイド情報
      ----------------------------------------------------- */}
    <details style={{ marginTop: '1.6rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        ステップ3：休憩時間を設定（エイド情報の入力）
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>

        {/* 導入文 */}
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          エイドでどれくらい休むかは、完走計画に大きく影響します。
          TrailRunSim では、エイドの基本情報は自動で登録済みです。あなたは<strong>休憩時間だけ入力すればOK</strong>です。
        </p>

        {/* 箇条書き（あなたの要望をすべて反映） */}
        <ul style={{ marginTop: '0.4rem' }}>
          <li>各エイド情報は、あらかじめ登録しているデータから自動で取得</li>
          <li>あなたが入力するのは「休憩時間（分）」だけ</li>
          <li>任意の場所をエイドとして追加も可能（山頂・絶景ポイントなど）</li>
          <li>登録済みの不要な地点は削除も可能</li>
          <li>
            自分用にカスタムしたい場合は「エイド情報を保存」をクリックすると、
            次回以降このレースを選択したときに自動で呼び出されます
          </li>
        </ul>

        {/* 補足 */}
        <p style={{ marginTop: '0.8rem', color: '#555', fontSize: '0.9rem' }}>
          特に中盤以降の長い休憩は、完走可否に直結します。
          レース戦略に合わせて、現実的な休憩時間を設定しましょう。
        </p>

      </div>
    </details>


    {/* -----------------------------------------------------
        ステップ4：シミュレーション実行
       ----------------------------------------------------- */}
    <details style={{ marginTop: '1.6rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        ステップ4：シミュレーション実行（完走計画の計算）
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          入力した「エイド情報」「走力情報」をもとに、
          TrailRunSim が<strong>各地点の通過予想時刻</strong>を自動で計算します。
        </p>

        <ul style={{ marginTop: '0.4rem' }}>
          <li>各地点の通過予想時刻を自動計算</li>
          <li>関門時間との差分をわかりやすく表示</li>
          <li>登り・下り・疲労の影響を反映した現実的な結果</li>
        </ul>

        <div
          style={{
            marginTop: '1rem',
            padding: '0.8rem',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
            lineHeight: 1.5
          }}
        >
          <strong style={{ fontSize: '0.95rem' }}>結果に応じた調整方法</strong>

          <p style={{ margin: '0.4rem 0', color: '#444' }}>
            関門に間に合わない場合は、次のいずれかを調整すると改善できます。
          </p>

          <ul style={{ marginTop: '0.4rem' }}>
            <li>「無理なく走れるペース」を少し速める</li>
            <li>「頑張り度」を上げて攻める設定にする</li>
          </ul>

          <p style={{ marginTop: '0.6rem', color: '#444' }}>
            逆に余裕がある場合は、以下の調整が可能です。
          </p>

          <ul style={{ marginTop: '0.4rem' }}>
            <li>エイドの休憩時間を増やす</li>
            <li>より現実的なレース計画に近づける</li>
          </ul>
        </div>

        <p style={{ marginTop: '0.8rem', color: '#555', fontSize: '0.9rem' }}>
          シミュレーション結果を見ながら、ペース・休憩・頑張り度を微調整することで、
          あなたの走力に最適な完走計画が完成します。
        </p>
      </div>
    </details>

    {/* -----------------------------------------------------
        ステップ5：結果の確認方法
       ----------------------------------------------------- */}
    <details style={{ marginTop: '1.6rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        ステップ5：結果を確認（グラフ・一覧表・地図・3D表示）
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          シミュレーション結果は「グラフ・一覧表・地図・3D」の4つのビューで確認できます。
        </p>

        <ul style={{ marginTop: '0.4rem' }}>
          <li>高低図グラフ：エイド通過時刻や関門時間をコース上にプロット</li>
          <li>エイド一覧表：累積距離・到着予測時刻・関門時間を一覧表示</li>
          <li>地図ビュー：ルート上で各エイド情報を確認し、地形の変化を把握</li>
          <li>3D表示：コース全体の高低差を立体的に可視化</li>
        </ul>

        {/* 気温・天気 */}
        <div
          style={{
            marginTop: '1rem',
            padding: '0.8rem',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
            lineHeight: 1.5
          }}
        >
          <strong style={{ fontSize: '0.95rem' }}>気温・天気の表示について</strong>

          <p style={{ margin: '0.4rem 0', color: '#444' }}>
            到着予測時刻に合わせて、その時刻の気温と天気アイコンを表示します。
          </p>

          <div
            style={{
              fontSize: '0.8rem',
              color: '#555',
              marginTop: '0.6rem'
            }}
          >
            <div>※ エイド距離は GPX と地図を見比べて算出しているため差異が出る場合があります。</div>
            <div>※ 気温・天気は OpenWeatherMap のデータを使用しています。</div>
            <div>
              ※ 天気アイコンの詳細は
              <a
                href="https://openweathermap.org/api/weather-conditions"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0066cc', fontWeight: 600, textDecoration: 'none' }}
              >
                OpenWeatherMap Weather Conditions
              </a>
              をご確認ください。
            </div>
          </div>

        </div>

        {/* 3Dビュー */}
<div
  style={{
    marginTop: '1rem',
    padding: '0.8rem',
    background: 'rgba(255,255,255,0.6)',
    borderRadius: '10px',
    lineHeight: 1.5
  }}
>
  <strong style={{ fontSize: '0.95rem' }}>3D表示について</strong>

  <p style={{ margin: '0.4rem 0', color: '#444' }}>
    「ルートを3Dで見る」を押すと、コース全体の高低差を立体的に確認できます。
    地形の起伏や登り・下りの強度を直感的に把握でき、レース戦略の精度が向上します。
  </p>

  <ul style={{ marginTop: '0.4rem', color: '#444' }}>
    <li>地形を 3D 化し、コースのアップダウンを立体表示</li>
    <li>ランナーがコース上を移動するアニメーションで走行イメージを再現</li>
    <li>距離・時間・標高をリアルタイム表示する HUD を搭載</li>
    <li>相対標高グラデーションで高度を色分け</li>
  </ul>


  <div style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.6rem' }}>
    ※ 3D表示は開発中のため、動作が重くなる場合があります。<br />
    ※ GPX の精度により、地形とのズレが生じることがあります。<br />
    ※ 現在、HUDに表示される「時間」は、フィニッシュタイムから逆算した経過時間を表示しているため、実際のレースでの上り・下り・疲労によるペース変化は反映されていません。（今後update予定）<br />
  </div>
</div>


        {/* 補足 */}
        <p style={{ marginTop: '0.8rem', color: '#555', fontSize: '0.9rem' }}>
          4つのビューを組み合わせて確認することで、レース全体の流れをより正確に把握できます。
        </p>
      </div>
    </details>

    {/* -----------------------------------------------------
        装備リンク
       ----------------------------------------------------- */}
    <p style={{ marginTop: '1.5rem', fontSize: '1rem', fontWeight: 'bold' }}>
      計画ができたら、次は装備を整えましょう →{' '}
      <Link to="/AffordableGear">軽量・コンパクトで実用的なおすすめ装備</Link>
    </p>

    {/* -----------------------------------------------------
        応用編：登山者向けガイド
       ----------------------------------------------------- */}
    <details style={{ marginTop: '2rem' }}>
      <summary
        style={{
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: '0.4rem 0'
        }}
      >
        応用編：登山者向けの使い方ガイド
      </summary>

      <div style={{ padding: '0.8rem 0', lineHeight: 1.6 }}>
        <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
          TrailRunSim はトレイルランニング向けに開発されていますが、
          <strong>一般登山の行動計画にも応用できる</strong>柔軟なシミュレーターです。
          GPX を読み込むだけで、標高・距離・地形の変化を把握でき、
          行動開始から日没までの安全な行程計画を立てることができます。
        </p>

        <ul style={{ marginTop: '0.4rem' }}>
          <li>GPX読み込みでルートの標高・距離・地形を自動取得</li>
          <li>「エイド」＝休憩ポイント（水場・山小屋・展望台など）として登録可能</li>
          <li>「関門時刻」＝通過予定時刻（例：●時までに●●岳）として活用</li>
          <li>開始〜終了時間は <strong>行動開始時間〜日没時間</strong> を設定</li>
          <li>ITRA係数は <strong>0</strong>（持久力補正を無効化）に設定</li>
        </ul>

        {/* 情報カード：登山ペース */}
        <div
          style={{
            marginTop: '1rem',
            padding: '0.8rem',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
            lineHeight: 1.5
          }}
        >
          <strong style={{ fontSize: '0.95rem' }}>登山向けのペース設定の目安</strong>

          <p style={{ margin: '0.4rem 0', color: '#444' }}>
            登山では荷物の重さや地形の影響が大きいため、
            トレランよりもゆっくりしたペース設定が必要です。
          </p>

          <ul style={{ marginTop: '0.4rem' }}>
            <li><strong>15分/km</strong>（体力のある登山者・整備された道）</li>
            <li><strong>20分/km</strong>（初心者・荷物が多い場合。一般的な山地図の標準コースタイム）</li>
          </ul>
        </div>

        <p style={{ marginTop: '0.8rem', color: '#555', fontSize: '0.9rem' }}>
          特に初心者登山やファミリー登山では、無理のないペース設定が安全につながります。
          TrailRunSim を活用して、安心できる行動計画を作ってみてください🧭
        </p>

      </div>
    </details>
    <p
      style={{
        marginTop: '2rem',
        fontSize: '1.00rem',
        color: '#000000',
        fontWeight: 600
      }}
    >
      TrailRunSim を使って完走計画を作る（シミュレーション画面へ）→ 
      <Link
        to="/"
        style={{
          marginLeft: '6px',
          color: '#2c7be5',
          textDecoration: 'none'
        }}
      >
        シミュレーションを開く
      </Link>
    </p>
  </div>
);

export default HowToUse;
